import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Loader2, Globe, Check, Sparkles, ArrowRight, ArrowLeft,
  Edit3, Palette, Type, Image, ExternalLink, LayoutDashboard,
  Eye, Undo2, ChevronDown, Send, Monitor, Smartphone, MousePointerClick, Save,
  PanelLeftOpen, X,
} from "lucide-react";
import { templateThemes, getCategories, getTemplatesByCategory, getThemeById } from "@/components/website-templates/templateThemes";
import type { TemplateTheme } from "@/components/website-templates/templateThemes";
import TemplateRenderer from "@/components/website-templates/TemplateRenderer";
import type { WebsiteCustomizations } from "@/components/website-templates/PremiumTemplate";
import TemplateGallery from "@/components/website-templates/TemplateGallery";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

type ViewMode = "gallery" | "editor";

const FONT_OPTIONS = [
  { name: "Inter", value: "Inter, sans-serif" },
  { name: "Playfair", value: "Playfair Display, serif" },
  { name: "Poppins", value: "Poppins, sans-serif" },
  { name: "Montserrat", value: "Montserrat, sans-serif" },
  { name: "Roboto Slab", value: "Roboto Slab, serif" },
];

const COLOR_PRESETS = [
  { name: "Doré", primary: "#d4a853", bg: "#0c0a09", text: "#ffffff" },
  { name: "Bleu", primary: "#3b82f6", bg: "#0f172a", text: "#ffffff" },
  { name: "Émeraude", primary: "#10b981", bg: "#0a0f0d", text: "#ffffff" },
  { name: "Violet", primary: "#8b5cf6", bg: "#0f0b1e", text: "#ffffff" },
  { name: "Rose", primary: "#ec4899", bg: "#1a0a12", text: "#ffffff" },
  { name: "Orange", primary: "#f59e0b", bg: "#1a1005", text: "#ffffff" },
  { name: "Clair", primary: "#2563eb", bg: "#ffffff", text: "#1e293b" },
  { name: "Crème", primary: "#92703a", bg: "#faf5ee", text: "#1e293b" },
];

export default function MyWebsitePage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState<ViewMode>("gallery");
  const [selectedTemplate, setSelectedTemplate] = useState<string>("");
  const [chatInput, setChatInput] = useState("");
  const [chatMessages, setChatMessages] = useState<{ role: string; content: string }[]>([]);
  const [chatLoading, setChatLoading] = useState(false);
  const chatRef = useRef<HTMLDivElement>(null);

  // Editor state
  const [showToolbar, setShowToolbar] = useState<"" | "edit" | "palette">("");
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [editableTitle, setEditableTitle] = useState("Mon Entreprise");
  const [editableDesc, setEditableDesc] = useState("Votre activité professionnelle");
  const [editableProduct, setEditableProduct] = useState("Notre service principal");
  const [editableCity, setEditableCity] = useState("France");
  const [previewMode, setPreviewMode] = useState<"desktop" | "mobile">("desktop");

  // Inline editing
  const [inlineEditMode, setInlineEditMode] = useState(false);
  const [customizations, setCustomizations] = useState<WebsiteCustomizations>({});
  const [planSite, setPlanSite] = useState<{ name: string; source: string; seen: boolean; reasoning?: string } | null>(null);

  // Style customization
  const [customFont, setCustomFont] = useState("Inter, sans-serif");
  const [customColors, setCustomColors] = useState(COLOR_PRESETS[0]);
  const [fontSize, setFontSize] = useState(16);

  // Load/save customizations from localStorage per template
  useEffect(() => {
    if (!selectedTemplate) return;
    try {
      const saved = localStorage.getItem(`grafton-website-${selectedTemplate}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        setCustomizations(parsed.customizations || {});
        if (parsed.title) setEditableTitle(parsed.title);
        if (parsed.desc) setEditableDesc(parsed.desc);
        if (parsed.product) setEditableProduct(parsed.product);
        if (parsed.city) setEditableCity(parsed.city);
      } else {
        setCustomizations({});
      }
    } catch {
      setCustomizations({});
    }
  }, [selectedTemplate]);

  // Site enregistré sur le serveur (préparé par Grafton depuis « Mon plan », ou sauvegardé depuis un autre appareil)
  useEffect(() => {
    if (!user) return;
    fetch(`${BACKEND_URL}/api/website/mine`, { credentials: "include" }).then((r) => (r.ok ? r.json() : null)).then((j) => {
      const site = j?.site;
      if (!site || !site.template_id) return;
      if (site.source === "plan" && !site.seen) {
        try {
          localStorage.setItem(`grafton-website-${site.template_id}`, JSON.stringify({ customizations: site.customizations || {}, title: site.name, desc: site.desc, product: site.product, city: site.city }));
        } catch { /* stockage indisponible */ }
        setSelectedTemplate(site.template_id);
        setEditableTitle(site.name); setEditableDesc(site.desc); setEditableProduct(site.product); setEditableCity(site.city || "France");
        setCustomizations(site.customizations || {});
        setView("editor");
        setPlanSite({ name: site.name, source: site.source, seen: false, reasoning: site.reasoning });
        fetch(`${BACKEND_URL}/api/website/mine/seen`, { method: "POST", credentials: "include" }).catch(() => undefined);
      } else if (!localStorage.getItem(`grafton-website-${site.template_id}`)) {
        try { localStorage.setItem(`grafton-website-${site.template_id}`, JSON.stringify({ customizations: site.customizations || {}, title: site.name, desc: site.desc, product: site.product, city: site.city })); } catch { /* ignoré */ }
      }
    }).catch(() => undefined);
  }, [user]);

  const persistWebsite = () => {
    if (!selectedTemplate) return;
    localStorage.setItem(`grafton-website-${selectedTemplate}`, JSON.stringify({
      customizations,
      title: editableTitle,
      desc: editableDesc,
      product: editableProduct,
      city: editableCity,
    }));
    fetch(`${BACKEND_URL}/api/website/mine`, { method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ template_id: selectedTemplate, name: editableTitle, desc: editableDesc, product: editableProduct, city: editableCity, customizations }) }).catch(() => undefined);
    toast.success("Modifications enregistrées");
  };

  const handleCustomize = (field: keyof WebsiteCustomizations, value: string) => {
    setCustomizations(prev => ({ ...prev, [field]: value }));
  };

  useEffect(() => { if (!loading && !user) navigate("/auth"); }, [loading, user, navigate]);
  useEffect(() => { chatRef.current?.scrollTo(0, chatRef.current.scrollHeight); }, [chatMessages]);

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  if (!user) return null;

  const theme = selectedTemplate ? getThemeById(selectedTemplate) : null;
  const categories = getCategories();

  const handleSelectTemplate = (id: string) => {
    setSelectedTemplate(id);
    const t = getThemeById(id);
    setEditableTitle(user.name || "Mon Entreprise");
    setView("editor");
    toast.success(`Template "${t.name}" sélectionné`);
  };

  // Detect section from free-text instruction — simple heuristic
  const detectSection = (text: string): "hero" | "cta" | "testimonial" | "service" | "footer" | "stats" => {
    const t = text.toLowerCase();
    if (/t[ée]moignage|avis|review/.test(t)) return "testimonial";
    if (/cta|appel.*action|bouton|call.to.action/.test(t)) return "cta";
    if (/service|produit|offre|product/.test(t)) return "service";
    if (/footer|pied.*page|pied/.test(t)) return "footer";
    if (/stat|chiffre|pourcent|%|\d+\+/.test(t)) return "stats";
    return "hero";
  };

  const handleEditorChat = async () => {
    if (!chatInput.trim() || chatLoading || !selectedTemplate) return;
    const msg = chatInput.trim();
    const section = detectSection(msg);
    setChatMessages(prev => [...prev, { role: "user", content: msg }]);
    setChatInput("");
    setChatLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/website/regenerate-section`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          template_id: selectedTemplate,
          section,
          instruction: msg,
          current_customizations: { ...customizations, name: editableTitle, desc: editableDesc, product: editableProduct, city: editableCity },
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const updates = data.updates || {};
      setCustomizations(prev => ({ ...prev, ...updates }));
      const summary = Object.keys(updates).length > 0
        ? `Section ${section} mise à jour (${Object.keys(updates).join(", ")}).`
        : `Aucune modification — reformule ton instruction.`;
      setChatMessages(prev => [...prev, { role: "assistant", content: summary }]);
      if (Object.keys(updates).length > 0) toast.success(`Section "${section}" régénérée`);
    } catch (e) {
      setChatMessages(prev => [...prev, { role: "assistant", content: "Échec de la régénération. Réessaie dans un instant." }]);
    }
    setChatLoading(false);
  };

  const handleChat = async () => {
    if (!chatInput.trim() || chatLoading) return;
    const msg = chatInput.trim();
    setChatMessages(prev => [...prev, { role: "user", content: msg }]);
    setChatInput("");
    setChatLoading(true);
    try {
      // AI Site Generator — Claude Sonnet 4.5 Hybride
      const res = await fetch(`${BACKEND_URL}/api/website/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt: msg,
          current_name: user?.name,
          user_id: user?.id || user?._id,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      // Apply generated content to editor state
      setSelectedTemplate(data.template_id);
      setEditableTitle(data.name || user?.name || "Mon Entreprise");
      setEditableDesc(data.desc || "Votre activité professionnelle");
      setEditableProduct(data.product || "Notre service principal");
      setEditableCity(data.city || "France");
      setCustomizations(data.customizations || {});
      // Apply primary color if provided
      if (data.customizations?.primaryColor) {
        setCustomColors(prev => ({ ...prev, primary: data.customizations.primaryColor }));
      }
      setChatMessages(prev => [...prev, { role: "assistant", content: `${data.reasoning || "Template généré."} Ouverture de l'éditeur…` }]);
      // Switch to editor view after brief delay so user reads the message
      setTimeout(() => {
        setView("editor");
        toast.success(`Site "${data.name}" généré par IA`);
      }, 800);
    } catch (e) {
      setChatMessages(prev => [...prev, { role: "assistant", content: "Désolé, la génération a échoué. Décris ton activité plus en détail et je réessaie." }]);
    }
    setChatLoading(false);
  };

  // ═══════ GALLERY VIEW ═══════
  if (view === "gallery") {
    return (
      <div className="min-h-screen bg-background" data-testid="my-website-page">
        {/* Header */}
        <div className="border-b border-border/50 bg-card/30 backdrop-blur px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-primary" />
            <h1 className="text-lg font-bold">Création de site internet</h1>
          </div>
          <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => navigate("/dashboard")}>
            <LayoutDashboard className="h-4 w-4" /> Dashboard
          </Button>
        </div>

        {/* Chat area — large like landing page */}
        <div className="max-w-3xl mx-auto px-4 py-10 space-y-4">
          <div className="text-center space-y-3 mb-6">
            <div className="h-14 w-14 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto">
              <Sparkles className="h-7 w-7 text-primary" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold">Quel site voulez-vous créer ?</h2>
            <p className="text-muted-foreground text-sm max-w-md mx-auto">
              Décrivez votre activité et Grafton IA vous recommandera le meilleur template.
            </p>
          </div>

          {/* Chat messages */}
          {chatMessages.length > 0 && (
            <div ref={chatRef} className="max-h-[200px] overflow-auto space-y-3 mb-4">
              {chatMessages.map((msg, i) => (
                <div key={i} className={`flex gap-3 ${msg.role === "user" ? "justify-end" : ""}`}>
                  {msg.role === "assistant" && <div className="h-7 w-7 rounded-lg bg-primary/15 flex items-center justify-center shrink-0 mt-0.5"><Sparkles className="h-3.5 w-3.5 text-primary" /></div>}
                  <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm ${msg.role === "user" ? "bg-primary text-primary-foreground" : "bg-card/80 border border-border/40"}`}>
                    {msg.content}
                  </div>
                </div>
              ))}
              {chatLoading && (
                <div className="flex gap-3"><div className="h-7 w-7 rounded-lg bg-primary/15 flex items-center justify-center shrink-0"><Sparkles className="h-3.5 w-3.5 text-primary" /></div><div className="bg-card/80 border border-border/40 rounded-2xl px-4 py-3"><div className="flex gap-1.5"><div className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce" /><div className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: "150ms" }} /><div className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: "300ms" }} /></div></div></div>
              )}
            </div>
          )}

          {/* Large textarea */}
          <div className="relative bg-card/80 border border-border/50 rounded-2xl shadow-[0_8px_40px_rgba(0,0,0,0.15)] backdrop-blur-sm focus-within:border-primary/40 transition-colors">
            <textarea
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleChat(); } }}
              placeholder="Ex: Je suis agent immobilier et je veux un site vitrine élégant..."
              rows={4}
              className="w-full resize-none bg-transparent px-5 pt-5 pb-14 text-sm placeholder:text-muted-foreground/50 focus:outline-none"
              data-testid="website-chat-input"
            />
            <div className="absolute bottom-3 right-3">
              <button
                className={`h-10 w-10 rounded-xl flex items-center justify-center transition-all ${chatInput.trim() ? "bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/25" : "bg-secondary/60 text-muted-foreground cursor-not-allowed"}`}
                onClick={handleChat}
                disabled={!chatInput.trim() || chatLoading}
                data-testid="website-chat-send"
              >
                {chatLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4 -rotate-90" />}
              </button>
            </div>
          </div>

          {/* Quick suggestions */}
          <div className="flex flex-wrap justify-center gap-2">
            {["Site immobilier de luxe", "E-commerce moderne", "Portfolio créatif", "Restaurant gastronomique", "SaaS B2B"].map(p => (
              <button key={p} onClick={() => { setChatInput(p); }} className="text-xs px-4 py-2 rounded-full border border-border/40 bg-card/40 text-muted-foreground hover:border-primary/40 hover:text-foreground transition-all">
                {p}
              </button>
            ))}
          </div>
        </div>

        {/* Modèles par secteur */}
        <div className="max-w-6xl mx-auto px-6 pb-16">
          <TemplateGallery selected={selectedTemplate} onSelect={handleSelectTemplate} />
        </div>
      </div>
    );
  }

  // ═══════ EDITOR VIEW ═══════
  return (
    <div className="h-screen bg-background flex flex-col overflow-hidden" data-testid="website-editor">
      {planSite && (
        <div className="bg-primary/10 border-b border-primary/30 px-4 py-2 text-xs flex items-center justify-between gap-3" data-testid="plan-site-banner">
          <span><b>Grafton a préparé une première version de votre site</b> à partir de votre plan. Relisez-la, ajustez les textes puis enregistrez.</span>
          <button className="text-muted-foreground hover:text-foreground" onClick={() => setPlanSite(null)} aria-label="Fermer">×</button>
        </div>
      )}
      {/* ─── Top Toolbar ─── */}
      <div className="h-12 border-b border-border/50 bg-card/80 backdrop-blur flex items-center justify-between px-2 md:px-4 shrink-0 z-50 gap-2">
        <div className="flex items-center gap-1.5 md:gap-3 min-w-0 flex-1">
          {/* Mobile: toggle sidebar */}
          <Button
            variant="ghost"
            size="sm"
            className="md:hidden gap-1 h-8 px-2 shrink-0"
            onClick={() => setMobileSidebarOpen(true)}
            data-testid="toolbar-open-sidebar"
            aria-label="Ouvrir les instructions IA"
          >
            <PanelLeftOpen className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" className="gap-1 h-8 text-xs px-2 shrink-0" onClick={() => setView("gallery")} data-testid="back-to-gallery">
            <ArrowLeft className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Templates</span>
          </Button>
          <div className="h-5 w-px bg-border/50 hidden sm:block" />
          <span className="text-sm font-semibold truncate">{theme?.name}</span>
          <Badge variant="outline" className="text-[9px] shrink-0 hidden sm:inline-flex">{theme?.category}</Badge>
        </div>

        <div className="flex items-center gap-1 md:gap-1.5 shrink-0">
          {/* Inline edit mode toggle */}
          <Button
            variant={inlineEditMode ? "default" : "outline"}
            size="sm"
            className="gap-1.5 h-8 text-xs px-2 md:px-3"
            onClick={() => setInlineEditMode(v => !v)}
            data-testid="toolbar-inline-edit"
            aria-label="Édition inline"
          >
            <MousePointerClick className="h-3.5 w-3.5" /> <span className="hidden md:inline">{inlineEditMode ? "Édition ON" : "Édition inline"}</span>
          </Button>

          {/* Palette button */}
          <Button
            variant={showToolbar === "palette" ? "default" : "outline"}
            size="sm"
            className="gap-1.5 h-8 text-xs px-2 md:px-3 hidden sm:flex"
            onClick={() => setShowToolbar(showToolbar === "palette" ? "" : "palette")}
            data-testid="toolbar-palette"
          >
            <Palette className="h-3.5 w-3.5" /> <span className="hidden md:inline">Style</span>
          </Button>

          {/* Identity panel toggle */}
          <Button
            variant={showToolbar === "edit" ? "default" : "outline"}
            size="sm"
            className="gap-1.5 h-8 text-xs px-2 md:px-3 hidden sm:flex"
            onClick={() => setShowToolbar(showToolbar === "edit" ? "" : "edit")}
            data-testid="toolbar-identity"
          >
            <Edit3 className="h-3.5 w-3.5" /> <span className="hidden md:inline">Identité</span>
          </Button>

          {/* Save */}
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 h-8 text-xs px-2 md:px-3"
            onClick={persistWebsite}
            data-testid="toolbar-save"
            aria-label="Enregistrer"
          >
            <Save className="h-3.5 w-3.5" /> <span className="hidden md:inline">Enregistrer</span>
          </Button>

          <div className="h-5 w-px bg-border/50 mx-1 hidden md:block" />

          {/* Preview mode — desktop only */}
          <Button variant={previewMode === "desktop" ? "default" : "ghost"} size="sm" className="h-8 w-8 p-0 hidden md:inline-flex" onClick={() => setPreviewMode("desktop")}>
            <Monitor className="h-3.5 w-3.5" />
          </Button>
          <Button variant={previewMode === "mobile" ? "default" : "ghost"} size="sm" className="h-8 w-8 p-0 hidden md:inline-flex" onClick={() => setPreviewMode("mobile")}>
            <Smartphone className="h-3.5 w-3.5" />
          </Button>

          <div className="h-5 w-px bg-border/50 mx-1 hidden md:block" />

          {theme?.htmlPath && (
            <Button variant="ghost" size="sm" className="gap-1 h-8 text-xs px-2 hidden md:inline-flex" onClick={() => window.open(BACKEND_URL + theme.htmlPath, "_blank")}>
              <ExternalLink className="h-3.5 w-3.5" /> Preview
            </Button>
          )}
          <Button variant="hero" size="sm" className="gap-1 h-8 text-xs px-2 md:px-3" data-testid="publish-btn">
            <Globe className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Publier</span>
          </Button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden relative">
        {/* Mobile backdrop */}
        {mobileSidebarOpen && (
          <div
            className="md:hidden fixed inset-0 bg-black/60 z-40"
            onClick={() => setMobileSidebarOpen(false)}
            data-testid="sidebar-backdrop"
          />
        )}

        {/* ─── Left Panel: Chat Instructions (static on desktop, drawer on mobile) ─── */}
        <aside
          className={`bg-card/95 md:bg-card/30 border-r border-border/50 flex flex-col shrink-0 transition-transform duration-200
            fixed md:static inset-y-0 left-0 z-50 md:z-auto
            w-[85vw] max-w-[320px] md:w-[300px]
            ${mobileSidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}
        >
          <div className="p-3 border-b border-border/30 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                <span className="text-xs font-semibold">Instructions IA</span>
              </div>
              <p className="text-[10px] text-muted-foreground mt-0.5">Demandez des modifications en langage naturel</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden h-7 w-7 p-0 shrink-0"
              onClick={() => setMobileSidebarOpen(false)}
              data-testid="sidebar-close"
              aria-label="Fermer"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Chat messages */}
          <div ref={chatRef} className="flex-1 overflow-auto p-3 space-y-3">
            {chatMessages.length === 0 && (
              <div className="text-center py-8">
                <Sparkles className="h-6 w-6 mx-auto text-muted-foreground/20 mb-2" />
                <p className="text-xs text-muted-foreground">Donnez vos instructions ici</p>
                <div className="space-y-1.5 mt-4">
                  {["Refais le hero plus émotionnel", "Régénère un témoignage client", "Change le CTA pour inciter au contact"].map(s => (
                    <button key={s} onClick={() => setChatInput(s)} className="block w-full text-left text-[10px] px-3 py-2 rounded-lg bg-muted/20 border border-border/20 text-muted-foreground hover:border-primary/20 transition-colors">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {chatMessages.map((msg, i) => (
              <div key={i} className={`flex gap-2 ${msg.role === "user" ? "justify-end" : ""}`}>
                {msg.role === "assistant" && <div className="h-5 w-5 rounded-md bg-primary/15 flex items-center justify-center shrink-0 mt-0.5"><Sparkles className="h-3 w-3 text-primary" /></div>}
                <div className={`max-w-[85%] rounded-xl px-3 py-2 text-xs ${msg.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted/30 border border-border/30"}`}>
                  {msg.content}
                </div>
              </div>
            ))}
          </div>

          {/* Chat input */}
          <div className="p-3 border-t border-border/30">
            <div className="flex gap-2">
              <Input
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleEditorChat(); }}
                placeholder="Ex: refais le hero plus émotionnel"
                className="text-xs h-9"
                data-testid="editor-chat-input"
              />
              <Button size="sm" className="h-9 w-9 p-0 shrink-0" onClick={handleEditorChat} disabled={!chatInput.trim() || chatLoading}>
                {chatLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              </Button>
            </div>
          </div>
        </aside>

        {/* ─── Center: Preview ─── */}
        <div className="flex-1 flex flex-col overflow-hidden bg-[hsl(222,28%,6%)]">
          {/* Edit toolbar panel — now labeled "Identité" */}
          {showToolbar === "edit" && (
            <div className="border-b border-border/30 bg-card/50 backdrop-blur p-3 space-y-3 shrink-0">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Identité de l'entreprise</p>
              <p className="text-[10px] text-muted-foreground/80 -mt-2">Ces infos remplissent le template. Pour éditer textes/titres directement, activez le mode <b>Édition inline</b>.</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-muted-foreground mb-1 block">Titre / Nom d'entreprise</label>
                  <Input value={editableTitle} onChange={(e) => setEditableTitle(e.target.value)} className="h-8 text-xs" data-testid="edit-title" />
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground mb-1 block">Description</label>
                  <Input value={editableDesc} onChange={(e) => setEditableDesc(e.target.value)} className="h-8 text-xs" data-testid="edit-desc" />
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground mb-1 block">Service / Produit</label>
                  <Input value={editableProduct} onChange={(e) => setEditableProduct(e.target.value)} className="h-8 text-xs" />
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground mb-1 block">Ville / Région</label>
                  <Input value={editableCity} onChange={(e) => setEditableCity(e.target.value)} className="h-8 text-xs" />
                </div>
              </div>
            </div>
          )}

          {/* Palette toolbar panel */}
          {showToolbar === "palette" && (
            <div className="border-b border-border/30 bg-card/50 backdrop-blur p-3 space-y-3 shrink-0">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Style & Couleurs</p>
              {/* Color presets */}
              <div>
                <label className="text-[10px] text-muted-foreground mb-2 block">Thème de couleurs</label>
                <div className="flex gap-2 flex-wrap">
                  {COLOR_PRESETS.map(c => (
                    <button
                      key={c.name}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs transition-all ${customColors.name === c.name ? "border-primary bg-primary/10" : "border-border/40 hover:border-primary/20"}`}
                      onClick={() => setCustomColors(c)}
                    >
                      <div className="h-3 w-3 rounded-full" style={{ background: c.primary }} />
                      <div className="h-3 w-3 rounded-full" style={{ background: c.bg }} />
                      {c.name}
                    </button>
                  ))}
                </div>
              </div>
              {/* Typography */}
              <div className="flex gap-4">
                <div className="flex-1">
                  <label className="text-[10px] text-muted-foreground mb-1 block flex items-center gap-1"><Type className="h-3 w-3" /> Typographie</label>
                  <div className="flex gap-1.5 flex-wrap">
                    {FONT_OPTIONS.map(f => (
                      <button
                        key={f.name}
                        className={`px-3 py-1.5 rounded-lg border text-xs transition-all ${customFont === f.value ? "border-primary bg-primary/10" : "border-border/40 hover:border-primary/20"}`}
                        style={{ fontFamily: f.value }}
                        onClick={() => setCustomFont(f.value)}
                      >
                        {f.name}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="w-[120px]">
                  <label className="text-[10px] text-muted-foreground mb-1 block">Taille texte: {fontSize}px</label>
                  <input
                    type="range"
                    min={12}
                    max={22}
                    value={fontSize}
                    onChange={(e) => setFontSize(parseInt(e.target.value))}
                    className="w-full accent-primary"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Inline edit hint banner */}
          {inlineEditMode && !theme?.htmlPath && (
            <div className="border-b border-primary/30 bg-primary/5 px-4 py-2 flex items-center gap-2 shrink-0">
              <MousePointerClick className="h-3.5 w-3.5 text-primary shrink-0" />
              <p className="text-xs text-primary/90">
                <b>Mode édition inline actif</b> — cliquez sur n'importe quel titre, texte ou bouton pour le modifier directement. Appuyez sur <kbd className="px-1 bg-primary/10 rounded text-[10px]">Entrée</kbd> pour valider, <kbd className="px-1 bg-primary/10 rounded text-[10px]">Échap</kbd> pour annuler.
              </p>
            </div>
          )}
          {inlineEditMode && theme?.htmlPath && (
            <div className="border-b border-amber-500/30 bg-amber-500/5 px-4 py-2 flex items-center gap-2 shrink-0">
              <MousePointerClick className="h-3.5 w-3.5 text-amber-500 shrink-0" />
              <p className="text-xs text-amber-500/90">
                L'édition inline n'est pas disponible sur ce template (rendu externe). Sélectionnez un template <b>Premium, E-commerce, Portfolio, Restaurant, SaaS ou Artisan</b> pour l'édition directe.
              </p>
            </div>
          )}

          {/* Preview */}
          <div className="flex-1 overflow-auto flex justify-center p-2 md:p-4">
            <div
              className={`transition-all duration-300 w-full ${previewMode === "mobile" ? "md:w-[390px] md:max-w-[390px]" : "max-w-[1200px]"}`}
              style={{
                fontFamily: customFont,
                fontSize: `${fontSize}px`,
                ["--preview-primary" as string]: customColors.primary,
                ["--preview-bg" as string]: customColors.bg,
                ["--preview-text" as string]: customColors.text,
              }}
            >
              {theme?.htmlPath ? (
                <iframe
                  key={`${theme.id}-${previewMode}`}
                  src={BACKEND_URL + theme.htmlPath}
                  title={theme.name}
                  className="w-full border-0 rounded-xl shadow-2xl"
                  style={{ height: "calc(100vh - 200px)", background: "#fff" }}
                />
              ) : (
                <div className="rounded-xl shadow-2xl overflow-hidden border border-border/20">
                  <TemplateRenderer
                    templateId={selectedTemplate}
                    name={editableTitle}
                    desc={editableDesc}
                    product={editableProduct}
                    client="Client"
                    city={editableCity}
                    sector="Général"
                    customizations={{ ...customizations, primaryColor: customColors.primary }}
                    editMode={inlineEditMode}
                    onCustomize={handleCustomize}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
